import { FC, useEffect, useState, useMemo, useCallback } from "react";
import { Box, Grid, useTheme } from "@mui/material";

// Data
import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
  useReportCommonStates,
  useCurrentUser
} from "hooks";
import {
  convertToNormalTime,
  generateReportFormInitialValues,
  generateReportFormValidationSchema,
  getStandardizedReportDates,
  reportTransformTimeForAPI
} from "app/data/helpers";
import {
  buildReportFilterPayload,
  getResetFilterFormValues
} from "app/data/helpers/ReportHelpers";
import {
  generateReportThunk,
  generateXarReportThunk
} from "app/features/reports/reportsActions";
import {
  getAllKeysByAccountThunk,
  resetReports,
  resetReportsFilterData,
  setReportsDateValues,
  setReportsFilterData
} from "app/features";
import {
  selectAccountFeaturesState,
  selectAccountKeysState
} from "app/data/selectors";
import ReportsMainTopbar from "components/common/reports/ReportsTopbar/ReportsMainTopbar";
import { compact } from "lodash";
import { ADMIN_ROLES } from "app/data/helpers/rolePermissions";
import ReportsCard from "components/common/reports/ReportsCard/ReportsCard";
import {
  CO2ReportIcon,
  GraphicalHoursIcon,
  GraphicalRidesIcon,
  MultipleRidesIcon,
  ActivityReportIcon,
  MilageReportIcon,
  WorkingHoursReportIcon,
  TagReportIcon,
  TotalCO2ReportIcon,
  XARReportIcon,
  ExceptionReportIcon
} from "assets/icons";
import GenerateReportForm from "components/forms/Reports/GenerateReportForm";
import { useNavigate } from "react-router-dom";

const Reports: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { currentRoleName } = useCurrentUser();
  const navigate = useNavigate();

  const { features } = useAppSelector(selectAccountFeaturesState);

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateValues,
    setDateValues,
    handleObjectsSearch,
    handleAccountSearch,
    handleOnChangeReportFilterForm,
    reportsFilterData,
    filterValues,
    setFilterValues,
    mergedObjectOptions,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates,
    searchedMinimalAccounts,
    searchedMinimalAccountsLoading
  } = useReportCommonStates();

  const [reportType, setReportType] = useState(2);
  const [reportUrl, setReportUrl] = useState("/reports/mileage-registration");

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const [apiPayload, setApiPayload] = useState(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? reportsFilterData
      : null
  );

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const handleDateChange = useCallback((value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  }, []);

  const handleDateRangeSubmit = () => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    let updatedFilterValues = { ...filterValues };

    if (filterValues.daysOfWeek) {
      updatedFilterValues = {
        ...filterValues,
        daysOfWeek:
          filterValues.daysOfWeek.length === 7
            ? []
            : filterValues.daysOfWeek.map(Number),
        startTime: reportTransformTimeForAPI(
          filterValues.startTime + ":00",
          dateValues.startDate,
          false
        ),
        endTime: reportTransformTimeForAPI(
          filterValues.endTime + ":00",
          dateValues.endDate,
          true,
          new Date(dateValues.startDate)
        )
      };
    }

    const payload = {
      ...(apiPayload ?? {}),
      startDate,
      endDate,
      ...updatedFilterValues,
      reportType: 2,
      rideModes: filterValues?.rideModes?.map(Number) || [],
      startedWithoutIgnition: true
    };

    setApiPayload(payload);
  };

  const handleCardClick = (card: any) => {
    if (card.reportType === 8) {
      navigate(card.link);
      return;
    }

    setFilterOpen(true);
    setReportType(card.reportType);
    setReportUrl(card.link);
  };

  const handleLoadReport = async (values: any) => {
    if (reportType === 8) {
      const xarPayload = {
        accountId: values.accountId,
        // forAccountId: values.accountId,
        objectIds: values.object,
        startDate: new Date(dateValues.startDate),
        endDate: new Date(dateValues.endDate),
        defaultDriverId: ""
      };

      const result = await dispatch(generateXarReportThunk(xarPayload));

      if (result.type === "reports/generateXarReportThunk/fulfilled") {
        // XAR report generated successfully
      }

      return;
    }

    setFilterValues(values);

    const payload = buildReportFilterPayload({
      values: {
        ...values,
        ...(values.rideModes ? { rideModes: values.rideModes.map(Number) } : {})
      },
      dateValues,
      reportType,
      extra: { startedWithoutIgnition: true }
    });

    const { type } = await dispatch(generateReportThunk(payload));

    if (type === "reports/generateReportThunk/fulfilled") {
      dispatch(setReportsFilterData(payload));
      dispatch(setReportsDateValues(dateValues));
      navigate(reportUrl);
    }
  };

  useEffect(() => {
    dispatch(resetReports());
    // handleDateRangeSubmit();
  }, [navigate, dispatch]);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);

  const reportsDropdown = useMemo(
    () =>
      compact([
        {
          name: t("reports.activityReport"),
          description: t(
            "reports.landingPage.summarizesTheActivitiesOfTheObjects"
          ),
          icon: <ActivityReportIcon />,
          link: "/dashboard/reports/activity-reports",
          reportType: 6
        },
        {
          name: t("reports.tagPosition"),
          description: t(
            "reports.landingPage.summarizesAllTagsAppliedToRidesOrVehicles"
          ),
          icon: <TagReportIcon />,
          link: "/dashboard/reports/tag-position",
          reportType: 1
        },
        {
          name: t("reports.workingHours"),
          description: t("reports.landingPage.tracksWorkingHoursForTheObjects"),
          icon: <WorkingHoursReportIcon />,
          link: "/dashboard/reports/working-hours",
          reportType: 3
        },
        {
          name: t("reports.graphicalWorkingHours"),
          description: t(
            "reports.landingPage.visualizedWorkingHoursOfTheObjects"
          ),
          icon: <GraphicalHoursIcon />,
          link: "/dashboard/reports/graphical-working-hours",
          reportType: 3
        },
        {
          name: t("reports.co2Report"),
          description: t("reports.landingPage.tracksCo2EmissionsFromRides"),
          icon: <CO2ReportIcon />,
          link: "/dashboard/reports/co2-reports",
          reportType: 4
        },
        ...(features.isRidesEnabled
          ? [
              {
                name: t("reports.graphicalRides"),
                description: t(
                  "reports.landingPage.visualizedRideDataForQuickAnalysis"
                ),
                icon: <GraphicalRidesIcon />,
                link: "/dashboard/reports/graphical-rides",
                reportType: 3
              },
              {
                name: t("reports.mileageRegistration"),
                description: t(
                  "reports.landingPage.vehicleMileageForTrackingAndMaintenancePurposes"
                ),
                icon: <MilageReportIcon />,
                link: "/dashboard/reports/mileage-registration",
                reportType: 2
              }
            ]
          : []),
        {
          name: t("reports.multipleRides"),
          description: t("reports.landingPage.multipleRideRoutesOnAMap"),
          icon: <MultipleRidesIcon />,
          link: "/dashboard/reports/multiple-rides",
          reportType: 5
        },
        {
          name: t("reports.totalCo2Report"),
          description: t("reports.landingPage.totalCo2Emissions"),
          icon: <TotalCO2ReportIcon />,
          link: "/dashboard/reports/total-co2-reports",
          reportType: 7
        },
        ...(features.isStationaryEnabled
          ? [
              {
                name: t("reports.stationaryReport"),
                description: t(
                  "reports.landingPage.stationaryReportDescription"
                ),
                icon: <ActivityReportIcon />,
                link: "/dashboard/reports/stationary-reports",
                reportType: 10
              }
            ]
          : []),
        ...(ADMIN_ROLES.includes(currentRoleName) && features.isKeurmerkEnabled
          ? [
              {
                name: t("reports.xarReport"),
                description: t("reports.landingPage.XARAuditFiles"),
                icon: <XARReportIcon />,
                link: "/dashboard/reports/xar-reports",
                reportType: 8
              }
            ]
          : []),
        ...(ADMIN_ROLES.includes(currentRoleName) && features.isKeurmerkEnabled
          ? [
              {
                name: t("reports.exceptionReport"),
                description: t("reports.landingPage.exceptionReport"),
                icon: <ExceptionReportIcon />,
                link: "/dashboard/reports/exception-reports",
                reportType: 9
              }
            ]
          : [])
      ]),
    [
      t,
      features.isRidesEnabled,
      features.isKeurmerkEnabled,
      features.isStationaryEnabled,
      currentRoleName
    ]
  );

  return (
    <Box>
      <ReportsMainTopbar title={t("typography.reports")} />
      <Box mx={4}>
        <Grid container spacing={2.5} alignItems={"stretch"}>
          {reportsDropdown?.map((card) => {
            return (
              <Grid
                key={card.name}
                item
                xs={12}
                sm={12}
                md={6}
                lg={4}
                xl={3}
                alignItems={"stretch"}
              >
                <ReportsCard
                  title={card.name}
                  description={card?.description}
                  icon={card?.icon}
                  onClick={() => handleCardClick(card)}
                />
              </Grid>
            );
          })}
        </Grid>
        <GenerateReportForm
          fetchOnDebounce={handleObjectsSearch}
          fetchAccountOnDebounce={handleAccountSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={handleLoadReport}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={mergedObjectOptions}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          theme={theme}
          isMileageReport={
            reportUrl === "/dashboard/reports/mileage-registration"
          }
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          formInitialValues={getHydratedFormInitialValues(
            generateReportFormInitialValues
          )}
          formValidations={generateReportFormValidationSchema}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(generateReportFormInitialValues)
            );
          }}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          dateRangeLoading={reportsLoading}
          reportType={reportType}
          searchedMinimalAccounts={searchedMinimalAccounts}
          searchedMinimalAccountsLoading={searchedMinimalAccountsLoading}
        />
      </Box>
    </Box>
  );
};

export default Reports;
